/***
*
*   DASHBOARD (HOME)
*   Operational overview of Law Cyborg
*
**********/

import { useState, useMemo } from 'react';
import { Card, Chart, Grid, Stat, Animate, useAPI } from 'components/lib';
import { useNavigate } from 'react-router-dom';

const PRESETS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
];

function toISO(date) {
  return date.toISOString().split('T')[0];
}

function buildDateParams(days) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return `?from=${toISO(from)}&to=${toISO(to)}`;
}

function formatChange(value) {
  if (value === null || value === undefined) return undefined;
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${value}%`;
}

// ─── localStorage helpers ────────────────────────────────────────────────────

const RECENTLY_VIEWED_KEY = 'mc_recently_viewed';

export function pushRecentlyViewed(entry) {
  // entry: { id, name, type: 'user'|'account' }
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    const current = raw ? JSON.parse(raw) : [];
    // Remove existing entry for this id
    const filtered = current.filter(e => !(e.id === entry.id && e.type === entry.type));
    // Prepend and cap at 5
    const updated = [entry, ...filtered].slice(0, 5);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function getRecentlyViewed() {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ─── Recently Viewed ─────────────────────────────────────────────────────────

function InitialsAvatar({ name, type }) {
  const initials = (name || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const bg = type === 'account' ? '#eff6ff' : '#f5f3ff';
  const color = type === 'account' ? '#1d4ed8' : '#6d28d9';

  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      background: bg, color, fontSize: 13, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

function TypeBadge({ type }) {
  const isAccount = type === 'account';
  return (
    <span style={{
      fontSize: 11, fontWeight: 600,
      padding: '2px 7px', borderRadius: 4,
      background: isAccount ? '#eff6ff' : '#f5f3ff',
      color: isAccount ? '#1d4ed8' : '#6d28d9',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {type}
    </span>
  );
}

function RecentlyViewed() {
  const navigate = useNavigate();
  const items = getRecentlyViewed();

  if (!items.length) {
    return (
      <div style={{ padding: '16px 0 4px', color: '#9ca3af', fontSize: 14 }}>
        No recently viewed items yet. Visit a user or account to populate this list.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
      {items.map(item => (
        <div
          key={`${item.type}-${item.id}`}
          onClick={() => navigate(`/${item.type === 'account' ? 'accounts' : 'users'}/${item.id}`)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '10px 14px', cursor: 'pointer', minWidth: 200, flex: '1 1 200px', maxWidth: 280,
            transition: 'box-shadow 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
          <InitialsAvatar name={item.name} type={item.type} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.name || item.email || '—'}
            </div>
            <TypeBadge type={item.type} />
          </div>
          <span style={{ fontSize: 13, color: '#6b7280', flexShrink: 0 }}>View →</span>
        </div>
      ))}
    </div>
  );
}

// ─── Quick Actions ────────────────────────────────────────────────────────────

const SHORTCUTS = [
  {
    label: 'Search Users',
    path: '/users',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="23" y1="21" x2="17" y2="15" />
        <circle cx="20" cy="18" r="3" />
      </svg>
    ),
  },
  {
    label: 'Search Accounts',
    path: '/accounts',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      </svg>
    ),
  },
  {
    label: 'View Logs',
    path: '/logs',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    label: 'View Feedback',
    path: '/logs',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

function QuickActions() {
  const navigate = useNavigate();

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
      {SHORTCUTS.map(s => (
        <button
          key={s.label}
          onClick={() => navigate(s.path)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
            padding: '10px 18px', cursor: 'pointer',
            fontSize: 14, fontWeight: 500, color: '#374151',
            flex: '1 1 150px',
            transition: 'box-shadow 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
          onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
          <span style={{ color: '#6b7280', display: 'flex' }}>{s.icon}</span>
          {s.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function Dashboard(props) {

  const [activeDays, setActiveDays] = useState(30);
  const params = useMemo(() => buildDateParams(activeDays), [activeDays]);

  const stats  = useAPI(`/api/metrics/summary${params}`);
  const growth = useAPI(`/api/metrics/accounts/growth${params}`);

  return (
    <Animate type='pop'>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>Home</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>
          Operational overview of Law Cyborg
        </p>
      </div>

      {/* Date range picker */}
      <div className='flex items-center gap-2 mb-6'>
        {PRESETS.map(p => (
          <button
            key={p.label}
            onClick={() => setActiveDays(p.days)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              activeDays === p.days
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Stat grid */}
      <Grid cols='4'>
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.totalAccounts }
          label='Total Accounts'
          icon='credit-card'
        />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.newAccounts }
          label='New Accounts'
          icon='user-plus'
          change={ formatChange(stats?.data?.newAccountsChange) }
        />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.totalUsers }
          label='Total Users'
          icon='users'
        />
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.newInvites }
          label='User Invites'
          icon='mail'
          change={ formatChange(stats?.data?.newInvitesChange) }
        />
        {/* TODO: wire Stripe subscription count */}
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.activeSubscriptions ?? '—' }
          label='Active Subscriptions'
          icon='check-circle'
        />
        {/* TODO: wire Stripe MRR */}
        <Stat
          loading={ stats?.loading }
          value={ stats?.data?.monthlyRevenue ?? '—' }
          label='Monthly Revenue'
          icon='credit-card'
        />
      </Grid>

      {/* Growth charts */}
      <Card title='Accounts Growth'>
        <Chart
          type='line'
          color='green'
          data={ growth?.data?.accounts }
          loading={ growth?.loading }
        />
      </Card>
      <Card title='Users Growth'>
        <Chart
          type='line'
          color='green'
          data={ growth?.data?.users }
          loading={ growth?.loading }
        />
      </Card>

      {/* Recently Viewed */}
      <Card title='Recently Viewed'>
        <RecentlyViewed />
      </Card>

      {/* Quick Actions */}
      <Card title='Quick Actions'>
        <QuickActions />
      </Card>

    </Animate>
  );
}
